/**
 * Testi italiani dei set di agenti: elenco, pagina, runtime, credenziali,
 * agenti e impostazioni (ADR 0008).
 *
 * `credentialForm.noticeTitle` e `noticeBody` dicono che la credenziale non si
 * rilegge: stanno sopra il form, non in un tooltip, e restano netti in ogni
 * lingua (`testi-ui-italiano` §3.8).
 */

/** Testi dei set. */
export const sets = {
  /** Elenco dei set. */
  list: {
    /** Titolo della pagina. */
    title: 'Tutti i set',
    /** Frase che dice a che cosa serve la pagina. */
    description:
      'Ogni set è un gruppo di agenti che lavora sui suoi progetti. Scrivi all’Institore del set per dargli un compito.',
    /** Pulsante e titolo della procedura di creazione. */
    create: 'Nuovo set',
    /** Che cosa fa la procedura. */
    firstRunDescription:
      'Nome, risorse, credenziale di Claude e, se vuoi, il primo progetto. L’anteprima mostra il set mentre lo componi.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei set non riuscito. Riprova.',
    /** Elenco vuoto per chi non può creare set. */
    empty: 'Nessun set di agenti.',
    /** Titolo dell'avviso quando il piano non ammette altri set (ADR 0023). */
    limitTitle: 'Limite del piano raggiunto',
    /** Spiegazione dell'avviso, con nome del piano e numero di set ammessi. */
    limitBody:
      'Il piano {{plan}} consente al massimo {{max}} set. Per crearne altri chiedi a un amministratore di cambiare il piano.',
    /** Nome accessibile della ricerca nell'elenco. */
    search: 'Cerca fra i set',
    /** Segnaposto della ricerca. */
    searchPlaceholder: 'Nome o slug',
    /** Filtro per stato. */
    statusFilter: 'Stato',
    /** Filtro per runtime. */
    runtimeFilter: 'Runtime',
    /** Nessun set con i filtri scelti. */
    noMatch: 'Nessun set corrisponde ai filtri.',
    /** Stato vuoto: pulsante che apre la procedura. */
    createFirst: 'Crea il primo set',
    /** Pulsante di riga che apre il relay. */
    openRelay: 'Apri l’Institore',
    /** Nome accessibile del pulsante di riga. */
    openRelayNamed: 'Apri l’Institore di {{name}}',
    /** Colonne della tabella. */
    columns: {
      /** Cose che chiedono un intervento (ADR 0030 §6). */
      todo: 'Da fare',
      /** Stato del pod. */
      runtime: 'Runtime',
      /** Numero di agenti. */
      agents: 'Agenti',
      /** Numero di progetti. */
      projects: 'Progetti',
    },
  },

  /**
   * Rifiuti di dominio di `agents` (409 con `code`), uno per codice emesso da
   * `services/agents/src/agents/service/errors.py`.
   */
  refusals: {
    /** Progetto nel volume di un altro set, senza repository. */
    project_hosted_elsewhere:
      'Il progetto è già nel volume di un altro set e non ha un repository da clonare. Aggiungi un repository al progetto, poi riprova.',
    /** Relay protetto. */
    relay_protected: 'L’Institore non si rinomina e non si cancella. Puoi solo fermarlo.',
    /** Archivio oltre 50 MiB. */
    upload_too_large: "L'archivio supera il limite di 50 MiB. Escludi altre cartelle e riprova.",
    /** Importazione già in corso. */
    upload_in_progress: "C'è già un'importazione in corso per questo progetto. Attendi che finisca, poi riprova.",
    /** Slug o nome già usato. */
    slug_taken: 'Il nome è già usato. Scegline un altro.',
    /** Credenziale del harness mancante. */
    credential_missing: 'Manca la credenziale del harness per questo set. Aggiungila nelle impostazioni, poi riprova.',
    /** Coda dei messaggi piena. */
    queue_full: 'Troppi messaggi in attesa in questo set. Attendi le risposte, poi riprova.',
    /** Messaggio già concluso. */
    message_closed: 'Il messaggio è già concluso e non si annulla più.',
    /** Agente in esecuzione. */
    agent_running: "Ferma l'agente prima di cancellarlo.",
    /** Progetto di casa. */
    home_project: 'È il progetto di casa del set e non si toglie.',
    /** Progetto già nel set. */
    already_in_set: 'Il progetto è già nel set.',
    /** Sito spento dal proprietario (risposta B3). */
    site_disabled: 'Il proprietario ha spento il sito: lo può riaccendere solo lui.',
    /** Ambiente di hosting già presente (ADR 0010 §6). */
    hosting_exists: 'Il progetto ha già un ambiente sul cluster. Aggiorna la pagina per vederlo.',
    /** Set con il progetto in sola lettura (ADR 0010 §6). */
    read_only_project:
      'Questo set ha il progetto in sola lettura: può consultare l’ambiente ma non crearlo né eliminarlo.',
    /** Piano senza altri set (ADR 0023). */
    plan_limit_sets:
      'Il tuo piano non consente altri set. Chiedi a un amministratore di cambiare il piano.',
    /** Set con tutti i progetti ammessi dal piano (ADR 0023). */
    plan_limit_projects:
      'Il set ha già tutti i progetti che il tuo piano consente. Togline uno o chiedi a un amministratore di cambiare il piano.',
    /** Token del mese esauriti sulla credenziale di institor (ADR 0023 §4). */
    token_limit_reached:
      'Hai usato i token del mese inclusi nel piano. Per continuare salva la tua credenziale Claude e riavvia il set, oppure attendi il primo del mese.',
    /** Progetto da zero con un'origine (ADR 0024). */
    greenfield_requires_empty: 'Un progetto da zero nasce vuoto. Togli repository e cartella, poi riprova.',
    /** Collaboratore esterno senza repository o collegamento (ADR 0024). */
    contributor_requires_git: 'Per collaborare a un progetto servono il repository e il tuo collegamento GitHub.',
    /** Affidamento senza obiettivo (ADR 0024). */
    brief_required: "Scrivi l'obiettivo del progetto.",
    /** Componente fuori dal piano (ADR 0024). */
    component_not_in_plan: 'Il tuo piano non comprende uno dei componenti scelti. Toglilo e riprova.',
    /** Hosting di un progetto da collaboratore esterno (ADR 0024). */
    hosting_not_allowed: 'Un progetto a cui collabori come esterno non ha un sito ospitato.',
    /** Hosting prima dell'approvazione dell'analisi (ADR 0024). */
    analysis_not_approved: "Il sito arriva dopo l'approvazione dell'analisi.",
    /** Analisi su un progetto non affidato (ADR 0024). */
    not_delegated: "Solo un progetto affidato ha un'analisi da approvare.",
    /** Analisi già decisa (ADR 0024). */
    analysis_closed: "L'analisi è già stata decisa. Aggiorna la pagina.",
    /** Nome dello spazio esistente non valido (ADR 0038). */
    invalid_namespace: 'Nome non valido. Usa minuscole, cifre e trattini.',
    /** Spazio di sistema o della piattaforma (ADR 0038). */
    namespace_forbidden: 'Questo spazio è del sistema o della piattaforma e non si collega.',
    /** Spazio non fra quelli ammessi dalla configurazione (ADR 0038). */
    namespace_not_allowed: 'Questo spazio non è fra quelli che si possono collegare.',
    /** Spazio inesistente (ADR 0038). */
    namespace_not_found: 'Lo spazio non esiste sul cluster. Controlla il nome.',
    /** Spazio già collegato a un altro progetto (ADR 0038). */
    namespace_linked: 'Lo spazio è già collegato a un altro progetto.',
    /** Indirizzo del sito non valido (ADR 0038). */
    invalid_site_url: 'Indirizzo non valido. Scrivi solo il nome del sito, senza percorso né porta.',
    /** Scrittura senza seconda conferma (ADR 0038). */
    write_not_confirmed: 'Per la scrittura ripeti il nome dello spazio.',
  },

  /** Pagina di un set. */
  page: {
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento del set non riuscito. Riprova.',
    /** Titolo per un set che non esiste. */
    notFoundTitle: 'Set non trovato',
    /** Spiegazione sotto il titolo. */
    notFoundSubtitle: "Il set non esiste più. Torna all'elenco e scegline un altro.",
    /** Collegamento all'elenco. */
    backToSets: 'Torna ai set',
    /** Schede della pagina. */
    tabs: {
      /** Conversazione con il relay. */
      conversation: 'Conversazione',
      /** Attività degli agenti. */
      activity: 'Attività',
      /** Siti dei progetti da vedere e annotare. */
      artifacts: 'Artefatti',
      /** Task del set (ADR 0029). */
      tasks: 'Task',
      /** Progetti del set. */
      projects: 'Progetti',
      /** Agenti del set. */
      agents: 'Agenti',
      /** Impostazioni del set. */
      settings: 'Impostazioni',
    },
  },

  /** Stato del runtime (il pod del set). */
  runtime: {
    /** In esecuzione. */
    running: 'In esecuzione',
    /** In avvio. */
    starting: 'In avvio',
    /** Fermo. */
    stopped: 'Fermo',
    /** In pausa dopo 30 minuti senza attività. */
    sleeping: 'In pausa',
    /** In errore. */
    failed: 'In errore',
    /** Nessun pod. */
    absent: 'Non avviato',
  },

  /** Avvisi del runtime che chiedono un intervento (ADR 0008, Appendice B). */
  runtimeNotice: {
    /** Set fermato perché uno strumento è stato tolto o ristretto. */
    tools_revoked: {
      /** Titolo. */
      title: 'Il set si riavvia: uno strumento è stato tolto o ristretto',
      /** Che cosa fare. */
      text: 'Un progetto del set ha perso uno strumento o parte dei suoi permessi. Il set riparte da solo con gli strumenti di adesso, e il lavoro interrotto riprende.',
    },
    /** Serve un riavvio. */
    restart_required: {
      /** Titolo. */
      title: 'Il set si riavvia da solo',
      /** Che cosa fare. */
      text: 'Gli strumenti sono cambiati o un container si è fermato per un errore. Il set riparte da solo: se gli agenti stanno lavorando aspetta che finiscano, se il container è caduto riparte subito e riprende il lavoro.',
    },
    /** Strumenti senza credenziale valida. */
    tools_not_configured: {
      /** Titolo. */
      title: 'Strumenti non pronti',
      /** Che cosa fare. */
      text: 'Un progetto del set usa uno strumento senza un token valido. Verifica la connessione nella pagina Strumenti: il set riparte da solo appena la connessione cambia.',
    },
    /** Servizio degli strumenti non raggiungibile. */
    tools_unavailable: {
      /** Titolo. */
      title: 'Strumenti non raggiungibili',
      /** Che cosa fare. */
      text: 'Il servizio degli strumenti non risponde. Il set riprova da solo ogni dieci minuti, quando gli agenti non stanno lavorando.',
    },
    /** Il messaggio non ha riacceso il set in pausa. */
    wake_failed: {
      /** Titolo. */
      title: 'Il set non si è riacceso',
      /** Che cosa fare. */
      text: 'Il messaggio è in coda, ma il set resta in pausa. Controlla la credenziale di Claude nelle impostazioni: il set riparte al prossimo messaggio.',
    },
    /** Messaggio arrivato a un set in pausa con gli agenti fermati dall'utente. */
    wake_stopped: {
      /** Titolo. */
      title: 'Agenti fermi',
      /** Che cosa fare. */
      text: 'Il messaggio è in coda, ma gli agenti del set sono fermi. Avvia il set per farli lavorare.',
    },
    /** Il pod gira con una versione vecchia: il set si aggiorna da solo. */
    runtime_outdated: {
      /** Titolo. */
      title: 'Aggiornamento in arrivo',
      /** Che cosa succede. */
      text: 'È uscita una versione nuova degli agenti. Il set si riavvia da solo per usarla appena gli agenti finiscono il lavoro in corso.',
    },
  },

  /** Riavvio del set. */
  restart: {
    /** Etichetta del pulsante. */
    label: 'Riavvia il set',
    /** Domanda della conferma. */
    question: 'Riavviare il set {{name}}?',
    /** Che cosa succede. */
    consequence: 'Gli agenti si fermano e ripartono con gli strumenti e le credenziali attuali. Il lavoro in corso si interrompe.',
    /** Esito riuscito. */
    done: 'Riavvio del set avviato.',
    /** Esito fallito. */
    failed: 'Riavvio non riuscito. Riprova.',
  },

  /** Taglie del pod. */
  size: {
    /** Taglia piccola. */
    small: 'Piccola',
    /** Taglia media. */
    medium: 'Media',
    /** Taglia grande. */
    large: 'Grande',
  },

  /** Nomi dei harness: nomi propri, non si traducono. */
  harness: {
    /** Claude Code. */
    'claude-code': 'Claude Code',
    /** Codex. */
    codex: 'Codex',
    /** OpenClaw. */
    openclaw: 'OpenClaw',
  },

  /** Natura di una credenziale. */
  credentialKind: {
    /** Token dell'abbonamento. */
    oauth_token: "Token dell'abbonamento",
    /** Chiave API. */
    api_key: 'Chiave API',
  },

  /** Stato della credenziale di Claude in un elenco. */
  credentials: {
    /** Presente. */
    configured: 'Configurata',
    /** Assente. */
    missing: 'Mancante',
  },

  /** Form della credenziale di un harness. */
  credentialForm: {
    /** Titolo del cassetto. */
    title: 'Credenziale di {{name}}',
    /** Pulsante di invio. */
    submit: 'Salva credenziale',
    /** Etichetta della scelta del tipo. */
    kind: 'Tipo di accesso',
    /** Opzione consigliata. */
    recommended: '{{label}} (consigliato)',
    /** Etichetta del campo per il token. */
    token: 'Token',
    /** Etichetta del campo per la chiave. */
    apiKey: 'Chiave API',
    /** Aiuto per un harness diverso da Claude Code. */
    otherHelp: 'Incolla la chiave API del fornitore del modello.',
    /** Token mancante all'invio. */
    tokenMissing: 'Incolla il token prima di salvare.',
    /** Chiave mancante all'invio. */
    apiKeyMissing: 'Incolla la chiave prima di salvare.',
    /** Titolo dell'avviso sopra il form. */
    noticeTitle: 'La credenziale non si rilegge',
    /** Che cosa succede al valore. */
    noticeBody:
      'Il valore viene cifrato e non torna più indietro, nemmeno a te. Per cambiarlo ne incolli uno nuovo.',
    /** Esito riuscito. */
    done: 'Credenziale salvata.',
    /** Salvataggio non riuscito. */
    failed: 'Salvataggio non riuscito. Riprova.',
  },

  /** Guida per ottenere la credenziale di Claude. */
  credentialGuide: {
    /** Token dell'abbonamento. */
    oauth: {
      /** Primo passo. */
      install: 'Installa Claude Code sul tuo computer, se non lo hai già:',
      /** Secondo passo. */
      run: 'In un terminale esegui',
      /** Terzo passo. */
      login: "Si apre il browser: accedi con l'account del tuo abbonamento Claude e autorizza.",
      /** Quarto passo. */
      copy: 'Il terminale stampa un token che inizia con sk-ant-oat. Copialo e incollalo qui sotto.',
      /** Nota finale. */
      note: "Gli agenti del set usano i limiti del tuo abbonamento, senza costi a consumo.",
    },
    /** Chiave API. */
    apiKey: {
      /** Primo passo. */
      open: 'Apri la pagina delle chiavi nella console di Anthropic:',
      /** Testo del collegamento. */
      openLink: 'console di Anthropic',
      /** Secondo passo. */
      create: 'Crea una chiave, copiala (inizia con sk-ant-api) e incollala qui sotto.',
      /** Nota finale. */
      note: "L'uso si paga a consumo sul conto della console.",
    },
  },
};

/** Testi della scheda «Agenti» di un set. */
export const setAgents = {
  /** Lettura non riuscita. */
  loadFailed: 'Caricamento degli agenti non riuscito. Riprova.',
  /** Lettura del catalogo dei ruoli non riuscita. */
  rolesFailed: 'Elenco dei ruoli non disponibile. Riprova.',
  /** Etichetta del relay nella tabella. */
  relayTag: 'Institore',
  /** Che cos'è il relay e che cosa non si può fare. */
  relayNote:
    'L’Institore nasce con il set e riceve i tuoi messaggi. Non si cancella e non si rinomina; puoi fermarlo.',
  /** Conseguenza dell'arresto del relay. */
  relayStopConsequence: 'I messaggi restano in coda finché non lo riavvii.',
  /** Nessun harness con credenziale. */
  noCredential: 'Nessun harness ha una credenziale. Aggiungila nelle impostazioni del set.',
  /** Colonne aggiuntive della tabella. */
  columns: {
    /** Harness dell'agente. */
    harness: 'Harness',
  },
  /** Cassetto del nuovo agente. */
  form: {
    /** Ruolo non scelto. */
    roleMissing: 'Scegli un ruolo.',
    /** Nome non valido. */
    nameInvalid: 'Usa minuscole, cifre e trattini, iniziando con una lettera.',
    /** Rifiuto del backend (422). */
    refused: 'Agente non aggiunto. Controlla nome, ruolo e harness.',
    /** Nome già usato nel set (409 `slug_taken`). */
    nameTaken: 'Il nome è già usato da un altro agente del set. Scegline un altro.',
    /** Esito fallito. */
    failed: 'Aggiunta non riuscita. Riprova.',
  },
};

/** Testi della scheda «Impostazioni» di un set. */
export const setSettings = {
  /** Sezione delle credenziali. */
  credentials: {
    /** Titolo della sezione. */
    title: 'Credenziali',
    /** Che cosa sono. */
    intro:
      'Una credenziale per harness. Senza una tua credenziale, Claude Code usa quella di institor inclusa nel piano. Il valore non si rilegge: si sostituisce.',
    /** Colonna della data. */
    updatedAt: 'Aggiornata il',
    /** Stato con la natura della credenziale. */
    configuredAs: 'Configurata: {{kind}}',
    /** Pulsante per aggiungerla. */
    add: 'Aggiungi',
    /** Pulsante per sostituirla. */
    replace: 'Sostituisci',
    /** Nome accessibile di «Aggiungi». */
    addNamed: 'Aggiungi la credenziale di {{name}}',
    /** Nome accessibile di «Sostituisci». */
    replaceNamed: 'Sostituisci la credenziale di {{name}}',
  },
  /** Sezione delle risorse. */
  resources: {
    /** Titolo della sezione e nome del form. */
    title: 'Risorse',
    /** Etichetta della taglia. */
    size: 'Taglia',
    /** Aiuto sotto la taglia. */
    sizeHelp: 'Decide CPU e memoria del set. Vale dal prossimo avvio.',
    /** Etichetta del profilo. */
    profile: 'Profilo degli agenti',
    /** Aiuto sotto il profilo. */
    profileHelp: 'Regole, subagenti e permessi con cui lavorano gli agenti.',
    /** Pulsante di invio. */
    submit: 'Salva',
    /** Esito riuscito. */
    done: 'Risorse aggiornate.',
    /** Esito fallito. */
    failed: 'Salvataggio non riuscito. Riprova.',
  },
  /** Sezione del ciclo di vita. */
  lifecycle: {
    /** Titolo della sezione. */
    title: 'Stato del set',
    /** Etichetta dello stato corrente. */
    current: 'Stato attuale',
    /** Set archiviato: niente da fare. */
    archived: 'Il set è archiviato e non si riattiva da qui.',
  },
  /** Eliminazione del set (2026-11-02). */
  deletion: {
    title: 'Elimina il set',
    intro: 'Se il set è stato creato per errore lo puoi eliminare. I progetti restano nella pagina «Progetti».',
    action: 'Elimina set',
    question: 'Eliminare il set «{{name}}»?',
    consequence: 'Gli agenti si fermano. Si cancellano conversazione, task e i siti dei progetti che nessun altro set usa. Non si torna indietro.',
    done: 'Set «{{name}}» eliminato.',
    failed: 'Eliminazione del set non riuscita. Riprova.',
  },
};

/** Forma dei testi dei set. */
export type Sets = typeof sets;

/** Forma dei testi della scheda agenti. */
export type SetAgents = typeof setAgents;

/** Forma dei testi delle impostazioni. */
export type SetSettings = typeof setSettings;
